import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../utils/api';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

/**
 * Dialog que aparece cuando la contraseña del usuario ha expirado
 * Bloquea el acceso hasta que se cambie la contraseña
 */
const ChangePasswordExpiredDialog = ({ 
  open, 
  userId, 
  userEmail, 
  expirationInfo,
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!formData.currentPassword) {
      toast.error('Debe ingresar su contraseña actual');
      return;
    }

    if (!formData.newPassword) {
      toast.error('Debe ingresar una nueva contraseña');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.newPassword === formData.currentPassword) {
      toast.error('La nueva contraseña debe ser diferente a la actual');
      return;
    }

    setLoading(true);
    
    try {
      // Llamar al endpoint especial para contraseñas expiradas
      const response = await authAPI.changePasswordExpired(
        userId,
        formData.currentPassword,
        formData.newPassword
      );

      toast.success('Contraseña actualizada correctamente. Ahora puede iniciar sesión.');
      
      // Limpiar formulario
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Callback de éxito
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al cambiar contraseña';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Validar que las contraseñas coincidan
  const passwordsMatch = formData.newPassword && 
                         formData.confirmPassword && 
                         formData.newPassword === formData.confirmPassword;

  return (
    <Dialog open={open} modal={true}>
      <DialogContent 
        className="max-w-lg"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold text-[#2D2D2D]">
                Cambio de Contraseña Requerido
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-sm text-gray-600">
            {expirationInfo?.warning || 'Su contraseña ha expirado por políticas de seguridad.'}
            <br />
            <strong>Debe establecer una nueva contraseña para continuar.</strong>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Email (solo informativo) */}
          <div className="p-3 bg-blue-50 rounded-md border border-blue-200">
            <div className="flex items-center gap-2 text-sm">
              <Lock className="w-4 h-4 text-blue-600" />
              <span className="text-blue-800">
                <strong>Usuario:</strong> {userEmail}
              </span>
            </div>
          </div>

          {/* Contraseña Actual */}
          <div>
            <Label htmlFor="currentPassword">
              Contraseña Actual <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                className="border-[#c5b8a2] pr-10"
                required
                autoFocus
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Nueva Contraseña */}
          <div>
            <Label htmlFor="newPassword">
              Nueva Contraseña <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                className="border-[#c5b8a2] pr-10"
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            {/* Indicador de fortaleza */}
            {formData.newPassword && (
              <div className="mt-3 p-3 bg-gray-50 rounded-md border border-gray-200">
                <PasswordStrengthIndicator 
                  password={formData.newPassword} 
                  email={userEmail}
                />
              </div>
            )}
          </div>

          {/* Confirmar Contraseña */}
          <div>
            <Label htmlFor="confirmPassword">
              Confirmar Nueva Contraseña <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className={`pr-10 ${
                  formData.confirmPassword && !passwordsMatch 
                    ? 'border-red-500' 
                    : 'border-[#c5b8a2]'
                }`}
                required
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            {/* Mensaje de coincidencia */}
            {formData.confirmPassword && (
              <p className={`text-xs mt-1 ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                {passwordsMatch ? '✓ Las contraseñas coinciden' : '✗ Las contraseñas no coinciden'}
              </p>
            )}
          </div>

          {/* Botón de submit */}
          <div className="flex justify-end pt-4 border-t">
            <Button 
              type="submit"
              className="bg-[#9d8272] hover:bg-[#7a6459] text-white px-6"
              disabled={loading || !passwordsMatch}
            >
              {loading ? 'Actualizando...' : 'Cambiar Contraseña'}
            </Button>
          </div>
        </form>

        {/* Nota de seguridad */}
        <div className="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
          <p className="text-xs text-yellow-800">
            <strong>⚠️ Nota de Seguridad:</strong> Por su seguridad, las contraseñas expiran cada 90 días. 
            Asegúrese de usar una contraseña fuerte y única.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordExpiredDialog;
